import type { SpinResponse } from "@slot/client-engine";
import { cannonFire } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

const mask = (indices: number[]): string =>
  Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/**
 * Tumbling reels — a "NICE" win reached by a single tumble. An L1 cluster wins 1000 and is removed;
 * the collapse drops in with no further win, then presents the minimal big-win overlay. spinWin
 * 1000 @ bet 100 = 10× → reaches nice(8×) but not big(15×): dim + scrolling number only, no title,
 * no angels, no settle bump, then a short auto-close (`BIG_WIN_TUNING.niceAutoCloseMs`). Final tier
 * "nice" is server-authoritative. Cluster/mask win, no payline.
 */
export function baseFixtureReelNiceWin(): SpinResponse {
  const win = mask([10, 11, 15, 16, 17]); // L1 ×5 (bottom-left block)
  const amountMinor = "1000";

  return spinFixture("reel-nice-win")
    .balance("EUR", "100000")
    // --- seq 0: reveal — L1 cluster wins 1000, removed ---
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter(amountMinor).gridRows([
        ["L4", "L5", "L2", "H1", "L3"],
        ["L3", "L4", "L5", "L2", "L5"],
        ["L1", "L1", "L4", "L5", "L2"],
        ["L1", "L1", "L1", "L3", "L4"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const w = fx.gridWinHighlight({
        positionMask: win, winAmount: amountMinor, symbolIdentifier: "L1", symbolCount: "5", // 500 × 2 = 1000 (nice tier)
        lines: [{ positionMask: win, winAmount: amountMinor, baseWinAmount: "500", multiplierApplied: "2" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win" });

      const fire = cannonFire(0);
      e.action(fire.type, fire.payload, { actionPhaseName: fire.phase, debugTag: "cannon_fire" });

      const r = fx.removeSymbolsByMask(win);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove" });

      const t = fx.updateTotalWinMeter(amountMinor);
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total" });
    })
    // --- seq 1: collapse — no further win; present the minimal "nice" overlay, then end ---
    .event("collapse", 1, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter(amountMinor).gridRows([
        ["L2", "L3", "L4", "H1", "L3"],
        ["L4", "L5", "L2", "L2", "L5"],
        ["L4", "L5", "L5", "L5", "L2"],
        ["L3", "L4", "L4", "L3", "L4"],
      ]);

      const bw = fx.presentBigWin({ levelIdentifier: "nice", spinWinAmount: amountMinor, betAmount: "100", winRatioLabel: "10x" });
      e.action(bw.type, bw.payload, { actionPhaseName: bw.phase, debugTag: "bigwin" });

      const end = fx.spinBoundaryEnd("base:1", amountMinor);
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
